/**
 * PlayerShell Component
 *
 * Shared full-screen player shell for quiz and sequencer pages.
 * Provides: back button, title area, progress indicator, informational timer.
 * Full-screen layout — no OperatorLayout wrapper (no tab bar, no header).
 * Mobile-first, bg-op-surface background.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock } from 'lucide-react';

interface PlayerShellProps {
  /** Title displayed below the progress bar (e.g. SOP + section name) */
  title: string;
  /** Subtitle (e.g. "Quiz" or "Step Sequencer") */
  subtitle?: string;
  /** Current question/step index (1-based) */
  current: number;
  /** Total questions/steps */
  total: number;
  /** Label prefix for progress (e.g. "Q" for quiz, "Step" for sequencer) */
  progressLabel?: string;
  /** Whether the player is in progress (shows leave confirmation on back) */
  inProgress?: boolean;
  /** Override back navigation target (defaults to /operator/content) */
  backTo?: string;
  /** Whether to show the timer */
  showTimer?: boolean;
  /** Optional externally-controlled elapsed seconds (overrides internal timer) */
  elapsedSeconds?: number;
  /** Page content */
  children: React.ReactNode;
}

/** Format seconds to MM:SS display */
function formatTimer(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function PlayerShell({
  title,
  subtitle,
  current,
  total,
  progressLabel = 'Q',
  inProgress = false,
  backTo = '/operator/content',
  showTimer = true,
  elapsedSeconds: externalElapsed,
  children,
}: PlayerShellProps) {
  const navigate = useNavigate();
  const [internalElapsed, setInternalElapsed] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Internal timer — only runs when externalElapsed is not provided
  useEffect(() => {
    if (externalElapsed !== undefined) return;

    timerRef.current = setInterval(() => {
      setInternalElapsed((prev) => prev + 1);
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [externalElapsed]);

  const elapsed = externalElapsed ?? internalElapsed;

  const handleBack = useCallback(() => {
    if (inProgress) {
      const confirmed = window.confirm('Leave practice? Your progress will be lost.');
      if (!confirmed) return;
    }
    navigate(backTo);
  }, [inProgress, navigate, backTo]);

  // Progress bar percentage
  const progressPercent = total > 0 ? Math.round((current / total) * 100) : 0;

  return (
    <div className="min-h-screen bg-op-surface">
      {/* Header bar */}
      <header className="sticky top-0 z-10 bg-op-surface/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-lg items-center justify-between px-4 py-3">
          {/* Back button */}
          <button
            type="button"
            onClick={handleBack}
            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl transition-colors duration-150 active:bg-gray-100 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
            aria-label="Go back"
          >
            <ArrowLeft size={20} strokeWidth={1.5} className="text-op-text-primary" />
          </button>

          {/* Progress indicator */}
          <span className="text-sm font-semibold text-op-text-primary">
            {progressLabel} {current} of {total}
          </span>

          {/* Timer */}
          {showTimer ? (
            <div className="flex min-h-[44px] min-w-[44px] items-center justify-center">
              <span className="flex items-center gap-1 text-sm tabular-nums text-op-text-tertiary">
                <Clock size={14} strokeWidth={1.5} />
                {formatTimer(elapsed)}
              </span>
            </div>
          ) : (
            <div className="min-h-[44px] min-w-[44px]" />
          )}
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-gray-100">
          <div
            className="h-full bg-guild-green transition-all duration-500 ease-out"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </header>

      {/* Title area */}
      <div className="mx-auto max-w-lg px-4 pt-4">
        {subtitle && (
          <p className="text-xs font-semibold uppercase tracking-widest text-op-text-tertiary">
            {subtitle}
          </p>
        )}
        <h1 className="mt-1 text-base font-semibold text-op-text-primary line-clamp-2">
          {title}
        </h1>
      </div>

      {/* Content area */}
      <div className="mx-auto max-w-lg px-4 pb-8 pt-4">
        {children}
      </div>
    </div>
  );
}

export { formatTimer };
